import type { Catalog } from "./en.js";

/**
 * CT-RM-30: Spanish (es) — human-written for every public-facing surface,
 * neutral Latin American Spanish in the formal "usted" register donors expect
 * from a nonprofit. Staff-app keys are intentionally absent (English fallback).
 * Never fill this file by machine translation.
 */
export const es: Catalog = {
  // ---- shared public chrome ----
  "common.cancel": "Cancelar",
  "common.firstName": "Nombre",
  "common.lastName": "Apellido",
  "common.email": "Correo electrónico",
  "common.phone": "Teléfono",
  "common.optional": "Opcional",
  "common.yourEmail": "Su correo electrónico",
  "common.emailMeLink": "Envíenme un enlace",
  "common.checkEmail": "Revise su correo",
  "common.processing": "Procesando…",
  "common.loading": "Cargando",
  "common.thankYou": "¡Gracias!",
  "switcher.label": "Idioma",

  // ---- hosted giving page ----
  "give.notFound": "No encontramos esta página de donaciones",
  "give.tagline": "Su donación hace posible nuestro trabajo. Gracias.",
  "give.demoBanner": "Modo de demostración: no se cobrará ningún pago y no se garantiza el envío de correos.",
  "give.checkoutComplete": "Gracias. Estamos procesando la confirmación de su pago. Le enviaremos el recibo por correo en cuanto se confirme.",
  "give.receivedOnce": "Recibimos su donación de {amount}.",
  "give.receivedMonthly": "Recibimos su donación mensual de {amount}.",
  "give.receiptCreated": "Se generó el recibo {number}. El envío por correo se registra por separado.",
  "give.once": "Donar una vez",
  "give.monthly": "Donar cada mes",
  "give.otherAmount": "Otro monto",
  "give.designate": "Destinar mi donación a",
  "give.whereNeeded": "Donde más se necesite",
  "give.emailForReceipt": "Correo electrónico (para su recibo)",
  "give.dedicate": "Dedicar esta donación (opcional)",
  "give.noDedication": "Sin dedicatoria",
  "give.inHonor": "En honor a",
  "give.inMemory": "En memoria de",
  "give.honoreeName": "Nombre",
  "give.coverFees": "Agregar {amount} para cubrir los costos de procesamiento y que el 100 % de mi donación llegue a la causa",
  "give.demoCard": "Modo de demostración: no se necesita tarjeta. En producción se usan los campos de tarjeta seguros de Stripe.",
  "give.submit": "Donar {amount}",
  "give.submitMonthly": "Donar {amount} al mes",
  "give.footer": "Donaciones seguras con CauseTide · Recibirá un recibo fiscal por correo electrónico",
  "give.unavailable": "Las donaciones en línea aún no están disponibles. Comuníquese con esta organización.",
  "give.minimum": "La donación mínima es de {amount}",

  // ---- builder landing pages ----
  "landing.notFound": "No encontramos esta página",
  "landing.renewLapsed": "Nos alegra verle de nuevo: su membresía venció el {date}. Renuévela a continuación.",
  "landing.renewCurrent": "Renueve su membresía: su periodo actual vence el {date}.",
  "landing.testMode": "Modo de prueba: no se procesa ningún pago ni se envía ningún correo.",
  "landing.choose": "Elija…",
  "landing.submitting": "Enviando…",
  "landing.oneTime": "Una vez",
  "landing.monthly": "Mensual",
  "landing.customAmount": "Otro monto",
  "landing.anonymous": "Donar de forma anónima (no mostrar mi nombre públicamente)",
  "landing.perMonth": "/mes",
  "landing.simulated": "Los pagos son simulados en este entorno.",
  "landing.receiptOnWay": "Le enviamos el recibo {number} a su correo.",
  "landing.testModeDone": "Modo de prueba: no se procesó ningún pago.",
  "embed.unavailable": "Este formulario no está disponible.",

  // ---- multi-step forms ----
  "steps.yourGift": "Su donación",
  "steps.yourDetails": "Sus datos",
  "steps.review": "Revisar",
  "steps.submit": "Enviar",
  "steps.progress": "Avance del formulario",
  "steps.completed": " (completado)",
  "steps.current": " (actual)",
  "steps.stepOf": "Paso {n} de {total}",
  "steps.fix.one": "Corrija esto antes de continuar:",
  "steps.fix.other": "Corrija estos {count} puntos antes de continuar:",
  "steps.frequency": "Frecuencia",
  "steps.giftAmount": "Monto de la donación",
  "steps.otherAmount": "Otro monto",
  "steps.required": " (obligatorio)",
  "steps.gift": "Donación",
  "steps.amountMonthly": "{amount} al mes",
  "steps.editGiftAmount": "el monto de la donación",
  "steps.edit": "Editar",
  "steps.yes": "Sí",
  "steps.back": "Atrás",
  "steps.next": "Siguiente",

  // ---- donación con carrito (CT-RM-31) ----
  "landing.cart.title": "Sus donaciones",
  "landing.cart.fund": "Fondo",
  "landing.cart.amount": "Monto",
  "landing.cart.frequency": "Frecuencia de la donación",
  "landing.cart.line": "Donación {n}",
  "landing.cart.addLine": "Agregar otro fondo",
  "landing.cart.removeLine": "Quitar la donación {n}",
  "landing.cart.remove": "Quitar",
  "landing.cart.otherAmount": "Otro importe",
  "landing.cart.subtotal": "Subtotal de las donaciones",
  "landing.cart.fees": "Comisiones de procesamiento",
  "landing.cart.total": "Total de hoy",
  "landing.cart.monthlyNote": "{amount} se repite cada mes",
  "landing.cart.coverFees": "Agregar {amount} para cubrir las comisiones de procesamiento y que el 100 % de mis donaciones llegue a la misión",
  "landing.cart.maxLines": "Hasta {count} donaciones",
  "landing.cart.empty": "Agregue una donación para comenzar",
  "landing.cart.submit": "{label}: {amount}",
  "landing.cart.lineSummary": "{fund}: {amount}",
  "landing.cart.lineSummaryMonthly": "{fund}: {amount} al mes",

  // ---- volunteer board ----
  "volunteer.loading": "Cargando oportunidades",
  "volunteer.unavailable": "Esta página de voluntariado no está disponible.",
  "volunteer.title": "Sea voluntario con nosotros",
  "volunteer.intro": "Elija el turno que mejor le convenga. Los horarios se muestran en {zone}.",
  "volunteer.none": "Por ahora no hay oportunidades de voluntariado abiertas. Vuelva a consultar pronto.",
  "volunteer.manageLink": "¿Ya se inscribió? Vea o cancele sus turnos",
  "volunteer.requirements": "Requisitos: ",
  "volunteer.noShifts": "No hay turnos próximos.",
  "volunteer.signUp": "Inscribirme",
  "volunteer.joinWaitlist": "Lista de espera",
  "volunteer.actionFor": "{action}: {when}",
  "volunteer.open": "Abierto",
  "volunteer.spotsLeft.one": "Queda {count} lugar",
  "volunteer.spotsLeft.other": "Quedan {count} lugares",
  "volunteer.fullWaitlist": "Completo: lista de espera abierta",
  "volunteer.full": "Completo",
  "volunteer.agreeRequired": "Para inscribirse, acepte los requisitos",
  "volunteer.waitlisted": "Está en la lista de espera.",
  "volunteer.waitlistedAt": "Está en la lista de espera (n.º {position}).",
  "volunteer.signedUp": "¡Ya está inscrito! Muchas gracias.",
  "volunteer.waitlistNote": "Si se libera un lugar, le avanzaremos automáticamente.",
  "volunteer.cancelNote": "¿Necesita cancelar? Use “Vea o cancele sus turnos” más abajo.",
  "volunteer.formLabel": "Inscripción de voluntariado",
  "volunteer.agree": "Leí y acepto los requisitos indicados arriba.",
  "volunteer.joinTheWaitlist": "Unirme a la lista de espera",
  "volunteer.confirm": "Confirmar inscripción",
  "volunteer.linkSent": "Si {email} tiene turnos de voluntariado con nosotros, le enviamos un enlace de un solo uso para gestionarlos.",
  "volunteer.devLink": "Abrir mis turnos (atajo de desarrollo)",
  "volunteer.requestLinkLabel": "Solicitar un enlace para sus turnos",
  "volunteer.backToBoard": "← Todas las oportunidades de voluntariado",
  "volunteer.backgroundCheck": "Se requiere verificación de antecedentes.",
  "volunteer.backgroundCheckNote": "Reservamos su lugar hasta que se apruebe su verificación de antecedentes. Le contactaremos con los próximos pasos.",
  "volunteer.pendingScreening": "Ya está inscrito, a la espera de una verificación de antecedentes.",
  "volunteer.pendingScreeningNote": "Reservamos su lugar mientras completamos una verificación de antecedentes. Nos pondremos en contacto.",

  // ---- peer-to-peer ----
  "p2p.loadingPage": "Cargando la página",
  "p2p.loadingFundraiser": "Cargando la página de recaudación",
  "p2p.loadingTeam": "Cargando la página del equipo",
  "p2p.unavailable": "Esta página de recaudación no está disponible.",
  "p2p.teamUnavailable": "Esta página de equipo no está disponible.",
  "p2p.raisedOfGoal": "recaudado de una meta de {goal}",
  "p2p.raised": "recaudado",
  "p2p.noOneYet": "Todavía no hay nadie. ¡Sea el primero!",
  "p2p.recentDonors": "Donantes recientes",
  "p2p.beFirst": "Sea la primera persona en donar.",
  "p2p.ends": "Termina el {date}",
  "p2p.starts": "Comienza el {date}",
  "p2p.campaignProgress": "Avance de la campaña",
  "p2p.fundraisers.one": "{count} recaudador",
  "p2p.fundraisers.other": "{count} recaudadores",
  "p2p.donors.one": "{count} donante",
  "p2p.donors.other": "{count} donantes",
  "p2p.members.one": "{count} integrante",
  "p2p.members.other": "{count} integrantes",
  "p2p.startFundraising": "Empezar a recaudar",
  "p2p.registrationClosed": "Inscripción cerrada",
  "p2p.manageMyPage": "Gestionar mi página",
  "p2p.topFundraisers": "Principales recaudadores",
  "p2p.topTeams": "Principales equipos",
  "p2p.goalInvalid": "Escriba una meta como 500 o 500.00",
  "p2p.chooseTeamError": "Elija un equipo al que unirse",
  "p2p.registrationComplete": "Inscripción completa",
  "p2p.pendingApproval": "¡Gracias! Su página está pendiente de aprobación.",
  "p2p.pageLive": "¡Su página de recaudación ya está publicada!",
  "p2p.linkEmailed": "Le enviamos por correo un enlace de un solo uso para editar su página.",
  "p2p.viewMyPage": "Ver mi página",
  "p2p.devManage": "Gestionar mi página (atajo de desarrollo)",
  "p2p.createTitle": "Cree su página de recaudación",
  "p2p.emailHint": "Nunca se muestra en su página",
  "p2p.yourGoal": "Su meta ({currency})",
  "p2p.pageTitle": "Título de la página",
  "p2p.pageTitlePlaceholder": "p. ej., Juana corre por ríos limpios",
  "p2p.story": "Su historia",
  "p2p.storyHint": "Opcional: por qué esta causa es importante para usted",
  "p2p.photo": "Enlace de la foto",
  "p2p.photoHint": "Opcional: la URL https:// de una imagen",
  "p2p.team": "Equipo",
  "p2p.teamOption": "Opción de equipo",
  "p2p.onMyOwn": "Recaudar por mi cuenta",
  "p2p.joinTeam": "Unirme a un equipo",
  "p2p.startTeam": "Crear un equipo (seré la persona capitana)",
  "p2p.chooseTeam": "Elija un equipo",
  "p2p.chooseTeamOption": "Elija un equipo…",
  "p2p.teamName": "Nombre del equipo",
  "p2p.createMyPage": "Crear mi página",
  "p2p.linkSent": "Si {email} tiene una página de recaudación en esta campaña, le enviamos un enlace de un solo uso para gestionarla.",
  "p2p.requestLinkLabel": "Solicitar un enlace para gestionar su página",
  "p2p.give": "Donar",
  "p2p.notAccepting": "Esta página no está recibiendo donaciones en este momento.",
  "p2p.givingUnavailable": "Las donaciones en línea aún no están disponibles.",
  "p2p.thankYouTitle": "Gracias",
  "p2p.thanksForGift": "¡Gracias por su donación de {amount}!",
  "p2p.receiptOnWay": "Le enviamos el recibo {number} a su correo.",
  "p2p.amountError": "Elija un monto de al menos 1.00",
  "p2p.donate": "Donar",
  "p2p.makeGift": "Haga una donación",
  "p2p.testMode": "Modo de prueba: no se cobra ninguna tarjeta.",
  "p2p.giftAmount": "Monto de la donación",
  "p2p.otherAmount": "Otro monto",
  "p2p.other": "Otro",
  "p2p.receiptEmailHint": "Para su recibo; nunca se muestra públicamente",
  "p2p.hideName": "Ocultar mi nombre y el monto en esta página",
  "p2p.giveAmount": "Donar {amount}",
  "p2p.frequency": "Frecuencia",
  "p2p.oneTime": "Una vez",
  "p2p.monthly": "Mensual",
  "p2p.giveMonthlyAmount": "Donar {amount} al mes",
  "p2p.thanksForMonthly": "¡Gracias por su donación mensual de {amount}! Ya se realizó el primer pago.",
  "p2p.message": "Deje un mensaje",
  "p2p.messageHint": "Opcional: se muestra con su donación en esta página",
  "p2p.messagePending": "Su mensaje aparecerá en la página cuando lo hayamos revisado.",
  "p2p.fundraisingFor": "{name} está recaudando fondos para {campaign}",
  "p2p.withTeam": " con el equipo {team}",
  "p2p.fundraiserProgress": "Avance de la recaudación",
  "p2p.personProgress": "Avance de {name}",
  "p2p.teamFundraisingFor": "Un equipo que recauda fondos para {campaign}",
  "p2p.captain": " · Capitán/a: {name}",
  "p2p.teamProgress": "Avance del equipo",
  "p2p.teamProgressOf": "Avance de {name}",
  "p2p.teamMembers": "Integrantes del equipo",
  "p2p.giveTo": "Donar a {name} ↗",
  "p2p.joinOrGive": "Participar o donar ↗",

  // CT-RM-33: Configuración › Seguridad (aprovisionamiento SCIM)
  "staff.security.title": "Seguridad",
  "staff.security.scimTitle": "Aprovisionamiento de usuarios con SCIM",
  "staff.security.scimIntro":
    "Permita que su proveedor de identidad (Okta, Microsoft Entra ID o cualquier cliente SCIM 2.0) cree, actualice y desactive usuarios de CauseTide. Los grupos se asignan a roles por nombre. Los usuarios desactivados conservan su historial; se revocan sus sesiones y tokens.",
  "staff.security.baseUrl": "URL base de SCIM",
  "staff.security.copy": "Copiar",
  "staff.security.copied": "Copiado al portapapeles",
  "staff.security.managedUsers.one": "{count} usuario está administrado por SCIM.",
  "staff.security.managedUsers.other": "{count} usuarios están administrados por SCIM.",
  "staff.security.tokens": "Tokens de SCIM",
  "staff.security.tokensHint": "Un token de SCIM solo puede llamar a la API de SCIM. Se muestra una sola vez; guárdelo en su proveedor de identidad.",
  "staff.security.newToken": "Nuevo token de SCIM",
  "staff.security.noTokens": "Todavía no hay tokens de SCIM.",
  "staff.security.colName": "Nombre",
  "staff.security.colToken": "Clave",
  "staff.security.colCreatedBy": "Creado por",
  "staff.security.colLastUsed": "Último uso",
  "staff.security.never": "nunca",
  "staff.security.revoke": "Revocar",
  "staff.security.revokeConfirm": "¿Revocar «{name}»? El aprovisionamiento con este token se detendrá de inmediato.",
  "staff.security.revoked": "Token de SCIM «{name}» revocado",
  "staff.security.createTitle": "Nuevo token de SCIM",
  "staff.security.tokenName": "Nombre",
  "staff.security.tokenNamePlaceholder": "p. ej., Okta producción",
  "staff.security.create": "Crear token",
  "staff.security.cancel": "Cancelar",
  "staff.security.nameRequired": "Asigne un nombre al token",
  "staff.security.createdTitle": "Su token de SCIM",
  "staff.security.createdLabel": "Token de SCIM",
  "staff.security.createdNote": "Copie este token en su proveedor de identidad ahora. No podrá volver a verlo.",
  "staff.security.sendInvites": "Enviar invitaciones a los usuarios creados por SCIM",
  "staff.security.sendInvitesHint": "Desactivado: los usuarios aprovisionados quedan activos de inmediato y no reciben invitación. Activado: pasan por el flujo de invitación habitual.",
  "staff.security.saved": "Guardado",
  "staff.users.managedBySCIM": "Administrado por SCIM",
  "staff.users.managedBySCIMHint": "El nombre, el correo y el estado provienen de su proveedor de identidad.",
  // staff.screening.*, staff.sync.* and staff.objects.* (CT-RM-34/35/36) are staff UI:
  // deliberately English-only — they fall back to the English catalog.
};
